import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { AdEditorAiPayload, AdVariant } from 'types/adEditorTypes';
import { AdChannel, AdCreativeType, AdPlacement } from 'types/adTypes';
import { useImmer } from 'use-immer';
import { AdEditorSideBarTabs } from 'views/ad-builder/AdBuilder';
import { Stack } from 'immutable';
import useNavigationContext from 'hooks/context/nav-context';
import { deleteTspan, getOverlay } from 'utils/overlayHelpers';
import { fabric } from 'fabric';
import {
  getCanvasHeight,
  getCanvasWidth,
  getOverlayFit,
} from 'utils/adEditorCanvasHelpers';
import { uniqueId } from 'lodash';
import { Crop } from 'react-image-crop';
import { CropMinimum } from 'components/video-gen/btlRefusal';
import {
  canWriteCapturedThumbnail,
  previewRenderIdentity,
  previewsAfterValueChange,
  railNeedsRequest,
  railPreviewKey,
  resolvedColoursFromPreview,
  sameSavedAds,
  STORED_PICTURE_FIELDS,
  storedPicturesApply,
  storedPicturesAdopt,
  templatePreviewInput,
  slotServesCanvas,
  tileSlotsFromPayload,
  variantAwaitsPhotoForPreview,
  withPhotoNeededNotices,
} from 'utils/templatePreviewHelpers';
import {
  createPreviewQueue,
  PreviewQueue,
  isPreviewBusy,
  previewFailureMessage,
  schedulePreview,
} from 'utils/templatePreviewQueue';
import {
  overlayForNewPhoto,
  NEW_AD_INHERITS_NOTHING,
  variantCanUseTemplates,
} from 'utils/templateFieldHelpers';
import {
  collectVariantIds,
  pickSelectionAfterInit,
} from 'utils/variantSelectionHelpers';
import { AdOverlay } from 'types/adEditorTypes';
import { useQuery } from 'hooks/sympl-query';
import { markAdVariantClipSeen } from 'api/adVariants';
import {
  GET_CUSTOM_AD_OVERLAYS,
  GET_DEFAULT_AD_OVERLAYS,
} from 'graphql/overlays/queries';
import {
  PREVIEW_AD_TEMPLATE,
  PREVIEW_UNSAVED_AD_TEMPLATE,
} from 'graphql/ad-variants/mutations';
import { useMutation } from 'hooks/sympl-mutation';
import useDebouncedCallback from 'hooks/debounceCallback';
import { useRefetchOnCustomerChange } from 'hooks/useRefetchOnCustomerChange';

import { createDelayedTabOpener } from './delayedTabOpener';
import { tabForObject } from './adEditorObjectTab';

/**
 * Server-rendered previews for the current variant, one slot per template code.
 *
 * Keyed by code rather than held as a single "current preview": the picker shows all six at once,
 * and a per-code slot also means a slow response lands in its own key instead of racing whatever
 * the customer clicked in the meantime.
 *
 * `error` carries the server's reason (capability off, unsupported placement, missing photo) so the
 * editor can say why rather than show a broken image.
 */
type TemplatePreview = {
  url?: string;
  loading?: boolean;
  error?: string;
  /** SYM-6214. Drawn full size, so fit for the canvas. See PreviewSlot.full. */
  full?: boolean;
  /** SYM-6247. Waiting out a long "busy" from the server; a retry is arranged. See PreviewSlot.waiting. */
  waiting?: boolean;
};

type TemplatePreviews = Record<string, TemplatePreview>;

/** SYM-6096. The minimum crop for Bring to Life, tied to the picture it was measured on. */
export interface BtlCropMinimum extends CropMinimum {
  /** The picture's path; a different picture means the numbers no longer apply. */
  path: string;
}

type AdEditorContextType = {
  activeTab: AdEditorSideBarTabs | undefined;
  currentVariant: AdVariant | undefined;
  payload: AdEditorAiPayload | undefined;
  previewChannel: AdChannel | undefined;
  defaultAdOverlays: AdOverlay[];
  customAdOverlays: AdOverlay[];
  setActiveTab: Dispatch<SetStateAction<AdEditorSideBarTabs | undefined>>;
  setPayload: Dispatch<SetStateAction<AdEditorAiPayload | undefined>>;
  setPreviewChannel: Dispatch<SetStateAction<AdChannel | undefined>>;
  setUploadMode: Dispatch<SetStateAction<boolean>>;
  /**
   * SYM-6096. What the crop has to cover for Bring to Life to accept THIS picture, set when the
   * server refused a crop and cleared when Bring to Life is opened again. The crop tool colours
   * its box by it. Null means the box is an ordinary crop box.
   */
  btlCropMinimum: BtlCropMinimum | null;
  setBtlCropMinimum: Dispatch<SetStateAction<BtlCropMinimum | null>>;
  initVariants: (variants: AdVariant[]) => void;
  /**
   * SYM-6214. Copy freshly drawn pictures onto the ads already in the editor, without rebuilding it.
   * Returns false when the answer describes a different set of ads; the caller then rebuilds.
   */
  applyStoredPictures: (variants: AdVariant[]) => boolean;
  /**
   * SYM-6351 (scene 8, D31). Set fields on several saved ads at once, as UNSAVED changes, so the
   * customer checks them and saves. Used by "Translate these texts". Keyed by ad id.
   */
  applyToVariants: (changes: Record<number, Partial<AdVariant>>) => void;
  deleteCurrentVariant: () => void;
  addVariantForPlacement: (placement: AdPlacement) => void;
  duplicateCurrentVariant: () => void;
  addVariant: (variant: AdVariant) => void;
  setCurrentVariant: (variant: AdVariant) => void;
  /** SYM-6076. Clears the "new clip" mark on a variant, here and on the server. */
  markClipSeen: (variant: AdVariant) => void;
  updateCurrentVariant: (
    key: keyof AdVariant,
    value: any,
    setIsDirty?: boolean
  ) => void;
  uploadMode: boolean;
  variants: AdVariant[];
  videoRef: React.RefObject<HTMLVideoElement>;
  svgStack: Stack<string>;
  addCanvasElement: (type: string) => void;
  setCanvas: (canvas: fabric.Canvas | null) => void;
  undoChange: () => void;
  changeOverlay: (overlay: string) => void;
  /**
   * SYM-5797: pick a SERVER-rendered v2 template. Unlike changeOverlay this does not touch the
   * canvas -- the canvas cannot draw these -- it records the choice and fetches a preview image.
   */
  selectTemplate: (overlay: AdOverlay) => void;
  /** Explicit pick of a legacy overlay; clears any v2 template the variant carried. */
  pickLegacyOverlay: (overlay: AdOverlay) => void;
  templatePreviews: TemplatePreviews;
  /** SYM-5975. One rendered picture per saved ad in the rail, keyed by uuid. */
  railPreviews: Record<string, string>;
  /** Render any of $codes that has no preview yet for this variant. Safe to call repeatedly. */
  ensureTemplatePreviews: (codes: string[]) => void;
  /** Re-render the selected template after a value changed. Debounced. */
  refreshTemplatePreview: () => void;
  canvasWidth: number;
  canvasHeight: number;
  /**
   * SYM-6006. Whether this customer has the v2 templates, published so that everything deciding
   * shape or gating on the feature reads ONE answer. Two independent readings can disagree while
   * the answer is still arriving, and a frame that disagrees with the save guard is worse than
   * either being wrong on its own.
   *
   * SYM-6051 made that stronger: the answer now comes from the subscription, which the SERVER
   * resolved, so the browser and the server can no longer disagree either.
   */
  hasAdTemplatesV2: boolean;
  getCanvasObject: (str: 'bg' | 'logo') => fabric.Object | undefined;
  activeObject: fabric.Object | undefined;
  updateActiveObject: (
    key: any, // TODO: keyof fabric.Object | fabric.Text | fabric.Textbox,
    value: any
  ) => void;
  removeActiveObject: () => void;
  duplicateActiveObject: () => void;
  cropBackground: (crop: Crop | null) => void;
  cropBackgroundComplete: (crop: Crop | null) => void;
  getBackgroundCrop: () => Crop | null;
  sendBackwards: () => void;
  bringForward: () => void;
};

export const AdEditorContext = createContext<AdEditorContextType>(
  {} as AdEditorContextType
);

/*
 * SYM-6247. What a tile says when the server stayed busy through every quiet retry.
 *
 * While the editor will still try again by itself it says so; once it has stopped, the message
 * names the one thing that does ask again -- a change to the ad -- instead of the old "open the
 * panel again", which asked for nothing.
 */
const BUSY_PREVIEW_MESSAGE = 'Busy right now. Trying again shortly.';
const BUSY_PREVIEW_FINAL_MESSAGE =
  'Still busy. Change anything in the ad to try again.';
/*
 * The whole recovery budget for one picture, unchanged inputs (Astra's eighth review asked for it
 * to be written down): each pass through the queue is 1 request + MAX_BUSY_RETRIES retries = 4,
 * and a tile that still ends busy heals BUSY_HEALS times, so 4 x (1 + 3) = 16 requests at most
 * before "Still busy" ends it for good. None of them goes out early: every request waits for the
 * cooldown the server last asked for (PreviewQueue.holdFor), so 16 is the ceiling on requests,
 * not a rate. Only a recognised busy answer (429 or 503) heals; a refusal with a reason does not.
 */
const BUSY_HEALS = 3;
const BUSY_HEAL_AFTER_MS = 30000;

const DEFAULT_FABRIC_PROPS = {
  hoverCursor: 'pointer',
  objectCaching: false,
  transparentCorners: false,
  cornerSize: 25,
  borderScaleFactor: 6,
  selectable: true,
  editable: true,
};

const DEFAULT_BG_IMG_PROPS = {
  lockMovementX: true,
  lockMovementY: true,
  lockScalingX: true,
  lockScalingY: true,
  lockRotation: true,
  hasControls: false,
  selectable: false,
};

// Monkeypatch loadSVGFromString to preserve custom text attributes.
// IMPORTANT: the 4th `options` param MUST be forwarded — it carries
// { crossOrigin: 'anonymous' } which Fabric.js passes to every <img>
// element it creates when loading SVG images. Without it, images load
// with sec-fetch-mode: no-cors, the canvas becomes tainted, and
// toDataURL() throws SecurityError (→ black images on publish). See SYM-5273.
const originalLoadSVGFromString = fabric.loadSVGFromString;

(fabric as any).loadSVGFromString = function (
  string: string,
  callback: any,
  reviver: any,
  options: any
) {
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(string, 'image/svg+xml');
  const customAlignMap = new Map();
  const canvasAlignmentMap = new Map();

  svgDoc.querySelectorAll('text[data-custom-align]').forEach((textElement) => {
    const customAlign = textElement.getAttribute('data-custom-align');
    if (customAlign)
      customAlignMap.set(textElement.textContent || '', customAlign);
  });

  svgDoc
    .querySelectorAll('text[data-canvas-alignment]')
    .forEach((textElement) => {
      const canvasAlignment = textElement.getAttribute('data-canvas-alignment');
      if (canvasAlignment)
        canvasAlignmentMap.set(textElement.textContent || '', canvasAlignment);
    });

  (originalLoadSVGFromString as any)(
    string,
    function (objects: fabric.Object[], svgOptions: any) {
      objects.forEach((obj) => {
        if (obj.type === 'text') {
          const textObj = obj as fabric.Text;
          const text = textObj.text || '';

          // text alignment
          if (customAlignMap.has(text)) {
            (textObj as any).customAlign = customAlignMap.get(text);
            textObj.set('textAlign', customAlignMap.get(text));
          }

          // position on canvas
          if (canvasAlignmentMap.has(text)) {
            const positionOnCanvas = canvasAlignmentMap.get(text);
            (textObj as any).positionOnCanvas = positionOnCanvas;
          }
        }
      });
      callback && callback(objects, svgOptions);
    },
    reviver,
    options
  );
};

export const AdEditorProvider: React.FC<ReactNode> = ({ children }) => {
  const [activeTab, setActiveTab] = useState<AdEditorSideBarTabs | undefined>(
    AdEditorSideBarTabs.VISUAL_OPTIONS
  );
  const [payload, setPayload] = useState<AdEditorAiPayload>();
  const [previewChannel, setPreviewChannel] = useState<AdChannel>();
  const [variants, setVariants] = useImmer<AdVariant[]>([]);
  const variantsRef = useRef<AdVariant[]>(variants);
  variantsRef.current = variants;
  const [uploadMode, setUploadMode] = useState(false);
  const [btlCropMinimum, setBtlCropMinimum] = useState<BtlCropMinimum | null>(
    null
  );
  const videoRef = useRef<HTMLVideoElement>(null);
  const [svgStack, setSvgStack] = useState(Stack<string>());
  const [defaultAdOverlays, setDefaultAdOverlays] = useState<AdOverlay[]>([]);
  const [templatePreviews, setTemplatePreviews] = useState<TemplatePreviews>(
    {}
  );
  /**
   * SYM-5975. One rendered picture per SAVED ad in the rail, keyed by railPreviewKey() (SYM-6213:
   * the database id plus what the server draws from, never the uuid a refetch regenerates).
   *
   * Separate from templatePreviews on purpose, and the two are not interchangeable.
   * templatePreviews is keyed by template CODE and belongs to the ad being edited: it is live, it
   * tracks unsaved edits, and it is thrown away whenever the ad underneath it changes. This map is
   * keyed by AD and is not live, which is exactly right for ads nobody is editing.
   *
   * Collapsing them into one map keyed by code was the original mistake: another ad's code
   * collides with the current one's, which is why the rail could only ever answer for a single ad
   * and every other thumbnail fell back to a canvas capture taken before the template existed.
   */
  const [railPreviews, setRailPreviews] = useState<Record<string, string>>({});
  /**
   * SYM-6213. Every rail render ever ASKED for, by the same key, whatever became of it.
   *
   * ⚠️ This reverses a choice SYM-5975 made on purpose, so the reason matters. That effect guarded
   * on the presence of a finished picture, so anything that cleared the map would refill it. But
   * the guard could not see a request still on its way, and the effect re-runs on every change to
   * `variants` -- which the canvas thumbnail capture makes on its own. Each capture while renders
   * were pending sent the whole rail again: 17 ads became 51 requests in a test on the real
   * provider, and on production 2026-09-15 it helped take the server down.
   *
   * The self-healing it gave up is not needed here: nothing clears rail pictures, and a changed ad
   * gets a new key rather than an old entry.
   */
  const railRequestedRef = useRef<
    Record<string, 'requested' | 'done' | 'failed'>
  >({});
  /**
   * SYM-6213. One queue for every preview this editor sends: at most three in flight, the open ad's
   * own template first, its other tiles next, the rail last.
   */
  const previewQueueRef = useRef<PreviewQueue>();
  if (!previewQueueRef.current) previewQueueRef.current = createPreviewQueue();
  /**
   * SYM-6213. The newest request per template code, so a slow answer to an older one cannot paint
   * over a newer picture. Typing into a value field sends a new request before the last has landed.
   */
  const latestTileRequestRef = useRef<Record<string, string>>({});
  // SYM-6247. How often a tile that ended "busy" has been cleared so it is asked for again, per ad
  // and design. Bounded, so a limit that never lifts ends in the message instead of a loop.
  const busyHealsRef = useRef<Record<string, number>>({});

  const [previewAdTemplate] = useMutation(PREVIEW_AD_TEMPLATE);
  const [previewUnsavedAdTemplate] = useMutation(PREVIEW_UNSAVED_AD_TEMPLATE);
  // Mirror of templatePreviews, so ensureTemplatePreviews() can ask "does this code already have a
  // slot?" without reading a stale closure.
  //
  // The guard is the PRESENCE OF A SLOT, not a separate set of "codes I have already requested".
  // That separate set was the bug behind two rounds of tiles stuck on "Rendering…": it could say
  // "already asked" while the previews it referred to had been cleared, and nothing would ever ask
  // again. Keyed off the slots themselves the state is self-healing -- whenever a preview is
  // missing, it gets requested, whatever cleared it and for whatever reason.
  const templatePreviewsRef = useRef<TemplatePreviews>({});
  // The preview payload is read from here, never from the render closure. SYM-5852 types into the
  // value fields and refreshes on a debounce; the debounced call closes over the state as it was
  // when the keystroke fired, which is one keystroke behind. Reading a ref sends what is actually
  // on screen instead of what was on screen a moment ago.
  const currentVariantRef = useRef<AdVariant | undefined>(undefined);
  /**
   * SYM-5921. Variant ids as of the last initVariants() call.
   *
   * A ref rather than state on purpose: it is read and rewritten inside initVariants and never
   * rendered, so holding it in state would cause a re-render for a value nothing displays.
   */
  const knownVariantIdsRef = useRef<Set<number>>(new Set());
  const [customAdOverlays, setCustomAdOverlays] = useState<AdOverlay[]>([]);
  const serializer = new XMLSerializer();

  const [canvas, setCanvas] = useState<fabric.Canvas | null>(null);

  const [activeObject, _setActiveObject] = useState<fabric.Object>();
  // Timeout so clicking an element doesn't become dragging when the sidebar opens
  const [tabOpener] = useState(() => createDelayedTabOpener(250));
  useEffect(() => tabOpener.cancel, [tabOpener]);

  const { currentVacancy, activeVacancy, customer, hasAdTemplates } =
    useNavigationContext();

  /**
   * SYM-6051. WHETHER this customer has the new ad templates.
   *
   * Read from the subscription, which the server resolves, instead of asking the feature-flag tool
   * from the browser. The server already resolves that flag for the catalogue, the preview and the
   * write path; asking it a second time here meant two readers of one answer that could disagree,
   * and the visible form of that was the picker offering a design the server then refused to draw.
   *
   * ⚠️ Declared HERE rather than beside the other template state above, because it now depends on
   * the navigation context being destructured first. Moving it back up gives a reference to a
   * `const` that does not exist yet.
   */
  const hasAdTemplatesV2 = hasAdTemplates;

  const [currentVariant, _setCurrentVariant] = useState<AdVariant>();
  // Always deep-clone when setting currentVariant so frozen Immer proxies
  // (from useImmer's `variants`) never leak into downstream code.
  const setCurrentVariant: typeof _setCurrentVariant = (action) => {
    if (typeof action === 'function') {
      _setCurrentVariant((prev) => {
        const result = action(prev);
        return result ? structuredClone(result) : result;
      });
    } else {
      _setCurrentVariant(action ? structuredClone(action) : action);
    }
  };

  // When updateCurrentVariant is called with key='svg', the canvas already
  // holds the correct state (the user just finished a drag/resize).
  // This ref stores the variant UUID to skip the reload only for the
  // same variant — a variant switch (different UUID) still reloads normally.
  const skipCanvasReloadRef = useRef<string | null>(null);

  /**
   * SYM-6059. The catalogue depends on WHICH customer is asking -- since SYM-6051 it holds only the
   * designs their plan includes -- but nothing about this query says so: it takes no variables, and
   * `useQuery` puts the customer in a header. Apollo therefore never asks again when the customer
   * changes underneath, and the picker went on offering the previous customer's designs until the
   * page was reloaded. Measured on QA 26 Aug 2026: 23138's seven designs stayed on screen after a
   * switch to 23135, who may have three.
   *
   * ⚠️ `fetchPolicy: 'network-only'` alone does NOT fix that. It decides what happens when a query
   * runs; it cannot make one run. The refetch below is what makes it run.
   */
  const { refetch: refetchDefaultOverlays } = useQuery<{
    defaultOverlays: AdOverlay[];
  }>(GET_DEFAULT_AD_OVERLAYS, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => setDefaultAdOverlays(data.defaultOverlays),
  });

  useRefetchOnCustomerChange(customer?.id, refetchDefaultOverlays);

  useQuery<{
    customOverlays: AdOverlay[];
  }>(GET_CUSTOM_AD_OVERLAYS, {
    onCompleted: (data) => setCustomAdOverlays(data.customOverlays),
    skip: !customer?.has_custom_overlays,
  });

  const duplicateCurrentVariant = () => {
    if (!currentVariant) return;
    addVariant({
      ...currentVariant,
      id: undefined,
      isDeleted: false,
      isDirty: true,
      uuid: uniqueId(),
    });
  };

  /**
   * The person has now looked at a clip that arrived while they were away. SYM-6076.
   *
   * Optimistic on purpose: the dot goes the moment they click, and the server call is a
   * background formality. If it fails the dot returns on the next load, which is the right way
   * round for a badge -- nobody should see an error for clicking on their own advert.
   */
  const markClipSeen = (variant: AdVariant) => {
    if (!variant.has_unseen_clip || !variant.id) return;

    setVariants((draft) => {
      const found = draft.find((v) => v.uuid === variant.uuid);
      if (found) found.has_unseen_clip = false;
    });

    markAdVariantClipSeen(variant.id);
  };

  const initVariants = (variants: AdVariant[]) => {
    setVariants(variants);

    /**
     * SYM-5921. The ids we knew BEFORE this refetch are what identify a variant the save has just
     * created, so they have to be read before being overwritten.
     *
     * A brand-new variant has no id until the save assigns one, so the old id match could never
     * find it and the selection fell through to the first variant of that placement. The customer
     * then edits an ad they did not choose, with nothing on screen to say so.
     */
    const previouslyKnownIds = knownVariantIdsRef.current;
    knownVariantIdsRef.current = collectVariantIds(variants);

    setCurrentVariant(
      pickSelectionAfterInit(variants, currentVariant, previouslyKnownIds)!
    );
  };

  const applyStoredPictures = (fresh: AdVariant[]): boolean => {
    if (!sameSavedAds(variants, fresh)) return false;

    const byId = new Map(fresh.map((v) => [v.id, v]));
    const copy = (target: AdVariant, source: AdVariant) => {
      STORED_PICTURE_FIELDS.forEach((field) => {
        (target as any)[field] = (source as any)[field];
      });
    };

    /*
     * SYM-6214 (Astra's third review, point 2.4). Per ad, and only for the save the editor holds.
     * An answer built before a save can land after it; copying save N's pictures onto the ad at
     * save N+1 would show the wrong pictures and, worse, clear `preview_pending`, so the editor
     * stopped waiting for the right ones. Such an ad is left alone; the next re-ask brings its own.
     */
    const sourceFor = (target: AdVariant | undefined | null) => {
      const source = target?.id ? byId.get(target.id) : undefined;
      return source &&
        target &&
        (storedPicturesApply(target, source) ||
          storedPicturesAdopt(target, source))
        ? source
        : undefined;
    };
    // SYM-6351: a server redraw moves the number on; the ad takes it so the next answer applies.
    const copyWithNumber = (target: AdVariant, source: AdVariant) => {
      copy(target, source);
      target.preview_generation = source.preview_generation;
    };

    setVariants((draft) => {
      draft.forEach((variant) => {
        const source = sourceFor(variant);
        if (source) copyWithNumber(variant, source);
      });
    });

    // SYM-6351: the ad on screen was redrawn by the server (its campaign's job title or location
    // changed). Its tiles show the old words, so they are dropped and drawn afresh -- after the
    // copy below, so the drawing step sees the new, still-empty stored pictures and asks for all.
    const onScreen = currentVariant?.id
      ? byId.get(currentVariant.id)
      : undefined;
    if (
      currentVariant &&
      onScreen &&
      !storedPicturesApply(currentVariant, onScreen) &&
      storedPicturesAdopt(currentVariant, onScreen)
    ) {
      setTemplatePreviews({});
    }

    // The displayed copy too: currentVariant is a separate clone (see applyResolvedColours).
    setCurrentVariant((prev) => {
      const source = sourceFor(prev);
      if (!prev || !source) return prev;
      const next = { ...prev };
      copyWithNumber(next, source);
      return next;
    });

    return true;
  };

  const applyToVariants = (changes: Record<number, Partial<AdVariant>>) => {
    const apply = (target: AdVariant) => {
      const change = target.id ? changes[target.id] : undefined;
      if (!change) return false;
      Object.assign(target, change);
      target.isDirty = true;
      return true;
    };

    setVariants((draft) => {
      draft.forEach((variant) => {
        apply(variant);
      });
    });

    // The displayed copy too: currentVariant is a separate clone (see applyStoredPictures).
    setCurrentVariant((prev) => {
      if (!prev) return prev;
      const next = { ...prev };
      return apply(next) ? next : prev;
    });
  };

  // Set preview channel to first selected channel if not set
  useEffect(() => {
    if (previewChannel) return;

    const fallbackChannel = currentVacancy?.channels.length
      ? currentVacancy?.channels[0]
      : AdChannel.FACEBOOK;

    setPreviewChannel(fallbackChannel);
  }, [previewChannel, currentVacancy]);

  /*
   * SYM-6006. The feed frame is portrait for a customer who has the v2 templates, and 1000x1000 for
   * everyone else. Per CUSTOMER, so the shape is fixed for the whole session and never changes
   * under someone mid-edit — see getCanvasHeight() for why that matters.
   *
   * ⚠️ hasAdTemplatesV2 belongs in the dependency arrays. PostHog resolves asynchronously, so it
   * arrives undefined and becomes true a moment later; without it here the canvas would be built at
   * 1000x1000 and never corrected. The effect at the bottom of this file already re-runs
   * canvas.setDimensions() whenever these two change, so the correction lands on its own.
   */
  const portraitFeed = Boolean(hasAdTemplatesV2);

  const canvasWidth = useMemo(
    () => getCanvasWidth(currentVariant?.placement, portraitFeed),
    [currentVariant?.placement, portraitFeed]
  );

  const canvasHeight = useMemo(
    () => getCanvasHeight(currentVariant?.placement, portraitFeed),
    [currentVariant?.placement, portraitFeed]
  );

  const getCanvasObject = (str: 'bg' | 'logo') =>
    canvas?.getObjects('image')[str === 'bg' ? 0 : 1];

  const getBackgroundCrop = (): Crop | null => {
    const background = getCanvasObject('bg') as fabric.Image | undefined;
    if (!background || !canvas) return null;

    const w = background.width ?? 0;
    const h = background.height ?? 0;
    if (!w || !h) return null;

    const left = background.left ?? 0;
    const top = background.top ?? 0;
    const scaleX = background.scaleX ?? 1;
    const scaleY = background.scaleY ?? 1;

    const xPct = (100 * (-left / scaleX)) / w;
    const yPct = (100 * (-top / scaleY)) / h;
    const widthPct = (100 * (canvasWidth / scaleX)) / w;
    const heightPct = (100 * (canvasHeight / scaleY)) / h;

    return {
      unit: '%',
      x: Math.max(0, xPct),
      y: Math.max(0, yPct),
      width: Math.min(100, widthPct),
      height: Math.min(100, heightPct),
    };
  };

  /**
   * Apply a crop to the canvas background image.
   * When `persist` is false (default) only the canvas is modified for live
   * preview during drag — no React state is touched so ReactCrop keeps
   * working without re-render interruptions.
   * When `persist` is true (called from onComplete / mouse-up) the crop,
   * svg, config and data_url are flushed to state.
   */
  const applyCropToCanvas = (crop: Crop | null, persist = false) => {
    if (!crop) return;

    /*
     * SYM-6230. The canvas half is optional; the crop itself is not.
     *
     * ⚠️ This used to return straight away when the canvas had no background photo, and that
     * silently threw the crop away. An ad built on a v2 template is stored with an empty canvas
     * layout (`config` `{"objects":[]}`, `svg` null -- ads 36637, 36706, 36707 and 36741 on QA,
     * 16 Sep 2026), and the canvas-load effect loads that empty layout, so there is never a photo
     * on the canvas. Steven moved the crop on QA: the box moved, the ad did not, "Unsaved changes"
     * never appeared and no preview request was sent -- on the 8 Sep build as well, so it predates
     * SYM-6213/6214. For a templated ad the canvas is covered by the server's picture anyway; the
     * crop only matters as a value the server draws with.
     */
    const background = getCanvasObject('bg') as fabric.Image | undefined;
    const imgEl = background?.getElement() as HTMLImageElement | undefined;
    const naturalW =
      imgEl?.naturalWidth ??
      currentVariant?.path?.width ??
      background?.width ??
      0;
    const naturalH =
      imgEl?.naturalHeight ??
      currentVariant?.path?.height ??
      background?.height ??
      0;

    if (background && naturalW && naturalH) {
      const { width, height, x, y } = crop;
      const scaleX = canvasWidth / (naturalW * (width / 100));
      const scaleY = canvasHeight / (naturalH * (height / 100));

      Object.assign(background, {
        left: -(naturalW * (x / 100) * scaleX),
        top: -(naturalH * (y / 100) * scaleY),
        scaleX,
        scaleY,
        clipPath: undefined,
        cropX: 0,
        cropY: 0,
        preserveAspectRatio: '',
      });

      // Always use requestRenderAll (async via rAF) — never synchronous
      // renderAll() which clears the canvas and redraws in one go.  If anything
      // goes wrong during the redraw the canvas stays permanently blank.
      canvas?.requestRenderAll();
    }

    if (persist) {
      updateCurrentVariant('crop', crop);
      // toSVG / toJSON read from the object model, not the pixel buffer,
      // so they return correct data even before the rAF render fires.
      // Guard against degenerate 1×1 canvas (placement not yet resolved), and skip it entirely
      // without a background: an SVG of an empty canvas would overwrite the ad's markup with nothing.
      if (
        background &&
        canvas &&
        canvas.getWidth() > 1 &&
        canvas.getHeight() > 1
      ) {
        const svg = canvas.toSVG();
        if (svg) updateCurrentVariant('svg', svg);
      }

      // SYM-5879. A v2 template is a picture rendered on the SERVER and laid over this canvas, so
      // re-rendering the canvas underneath changes nothing the customer can see. Reframing is a
      // value change like any other in the panel, and needs the same refresh — without it the ad
      // keeps the framing it was last rendered with and the drag appears to do nothing.
      // Debounced in the same call, so this fires once per drag, not once per pixel.
      refreshTemplatePreview();
    }
  };

  /** Live preview — fires on every pixel of the drag. */
  const cropBackground = (crop: Crop | null) => applyCropToCanvas(crop, false);

  /** Persist — fires once on mouse-up (onComplete). */
  const cropBackgroundComplete = (crop: Crop | null) =>
    applyCropToCanvas(crop, true);

  /**
   * Generate a data_url thumbnail for a variant by capturing the current canvas.
   * Mutates the variant object in place (preserving reference equality with
   * currentVariant) and triggers a re-render via a new array reference.
   *
   * IMPORTANT: crossOrigin must already be set on the image elements at load
   * time (see loadFromJSON / setCanvasTo).  Setting it here after the fact
   * invalidates the cached image data and causes the capture to produce a
   * solid-colour frame.
   */
  const generateDataUrl = (variantUuid: string) => {
    if (!canvas) return;

    const images = canvas
      .getObjects()
      .filter(({ type }) => type === 'image') as fabric.Image[];

    const expectedVariant = variantsRef.current.find(
      ({ uuid }) => uuid === variantUuid
    );
    const expectedBackgroundUrl =
      expectedVariant?.path &&
      expectedVariant.creative_type !== AdCreativeType.VIDEO &&
      expectedVariant.path.type !== AdCreativeType.VIDEO
        ? expectedVariant.path.path
        : undefined;
    const normaliseUrl = (url: string) => {
      try {
        return new URL(url, window.location.href).href;
      } catch {
        return url;
      }
    };
    const hasExpectedBackground = () =>
      !expectedBackgroundUrl ||
      images.some((image) => {
        const htmlImg = image.getElement() as HTMLImageElement;
        return (
          normaliseUrl(htmlImg.src) === normaliseUrl(expectedBackgroundUrl)
        );
      });

    // Fabric drops an SVG image object when its CORS load fails. In that case
    // the remaining transparent canvas (logo/banner/text) would be encoded as
    // a black JPEG. Never replace a variant preview with that partial render.
    if (!hasExpectedBackground()) {
      console.error(
        'generateDataUrl: expected background image is missing from canvas'
      );
      canvas.requestRenderAll();
      return;
    }

    // Wait until every image element is fully decoded before capturing.
    Promise.all(
      images.map((imgObj) => {
        const htmlImg = imgObj.getElement() as HTMLImageElement;
        if (htmlImg.complete) {
          return htmlImg.naturalWidth > 0
            ? Promise.resolve()
            : Promise.reject(
                new Error(`Canvas image failed to load: ${htmlImg.src}`)
              );
        }
        return new Promise<void>((resolve, reject) => {
          htmlImg.addEventListener('load', () => resolve(), { once: true });
          htmlImg.addEventListener(
            'error',
            () =>
              reject(new Error(`Canvas image failed to load: ${htmlImg.src}`)),
            { once: true }
          );
        });
      })
    )
      .then(() => {
        try {
          /*
           * SYM-6023. The canvas may have moved on while the images were decoding, so check it
           * still belongs to the ad this capture was started for. Writing a picture of the WRONG
           * ad is what this prevents -- see canWriteCapturedThumbnail().
           */
          if (
            !canWriteCapturedThumbnail(
              variantUuid,
              currentVariantRef.current?.uuid
            )
          ) {
            canvas?.requestRenderAll();

            return;
          }

          if (!hasExpectedBackground()) {
            throw new Error('Expected background disappeared before capture');
          }

          const dataUrl = canvas.toDataURL({ format: 'jpeg' });

          // Update data_url inside Immer's produce() to avoid mutating frozen proxies.
          // Direct mutation of variantsRef.current elements throws TypeError
          // because useImmer deeply freezes all objects outside of produce().
          setVariants((draft) => {
            const v = draft.find((d) => d.uuid === variantUuid);
            if (v && dataUrl) {
              v.data_url = dataUrl;
            }
          });
        } catch (error) {
          // Tainted canvas (CORS) or other error — keep the old data_url.
          console.error('generateDataUrl: capture failed', error);
        }
      })
      .catch((error) => console.error('generateDataUrl: capture failed', error))
      .finally(() => {
        // Schedule a render to restore the canvas after toDataURL (which may
        // read from the pixel buffer). Never use synchronous renderAll() here.
        canvas?.requestRenderAll();
      });
  };

  const updateCurrentVariant = (key: keyof AdVariant, value: any) => {
    if (!currentVariant) return;

    const currentUuid = currentVariant.uuid;
    // Default crop that mimics the effect of a first, natural resize:
    // center-crop to canvas aspect without stretching.
    const getDefaultCoverCrop = (resource: any): Crop | null => {
      const w = resource?.width ?? 0;
      const h = resource?.height ?? 0;
      if (!w || !h || !canvasWidth || !canvasHeight) return null;

      const imageAspect = w / h;
      const targetAspect = canvasWidth / canvasHeight;

      if (
        !isFinite(imageAspect) ||
        !isFinite(targetAspect) ||
        targetAspect <= 0
      )
        return null;

      // image is wider than target -> crop left/right
      if (imageAspect > targetAspect) {
        const widthPct = (targetAspect / imageAspect) * 100;
        return {
          unit: '%',
          x: (100 - widthPct) / 2,
          y: 0,
          width: widthPct,
          height: 100,
        };
      }

      // image is taller than target -> crop top/bottom
      const heightPct = (imageAspect / targetAspect) * 100;
      return {
        unit: '%',
        x: 0,
        y: (100 - heightPct) / 2,
        width: 100,
        height: heightPct,
      };
    };

    if (key === 'svg') setSvgStack((prev) => prev.push(currentVariant.svg!));

    setVariants((draft) => {
      const variant = draft.find((v) => v.uuid === currentUuid);
      if (!variant) return;

      (variant[key] as any) = value;
      variant.isDirty = true;

      if (key === 'path') {
        const resource = value;
        const defaultCrop =
          resource?.type === 'image' ? getDefaultCoverCrop(resource) : null;

        variant.creative_type = resource?.type;
        variant.crop = defaultCrop;
        // Clear persisted preview state; it belongs to the previous creative.
        // The canvas/overlay regeneration will rebuild these.
        variant.data_url = undefined;
        variant.config = undefined;

        if (resource?.type === 'video') {
          variant.logo = null;
          variant.svg = null;
        }

        if (resource?.type === 'image') {
          // Force overlay regeneration for a new image; old SVG references
          // the previous IMAGE_URL and can otherwise be reloaded onto the canvas.
          variant.svg = null;
        }

        if (resource === null) {
          variant.svg = null;
        }
      }

      if (key === 'svg') {
        // Config
        const json = canvas?.toJSON(
          Object.keys({ ...DEFAULT_FABRIC_PROPS, ...DEFAULT_BG_IMG_PROPS })
        );
        variant.config = JSON.stringify(json);
      }
    });

    // When key is 'svg', the canvas already shows the correct state (the user
    // just finished a drag/resize).  Tell the useEffect not to reload from config.
    // Store the UUID so we only skip for this variant — a variant switch still reloads.
    if (key === 'svg') {
      skipCanvasReloadRef.current = currentUuid;
    }

    // Keep currentVariant in sync for preview (without storing Immer drafts).
    setCurrentVariant((prev) => {
      if (!prev || prev.uuid !== currentUuid) return prev;

      const updated: AdVariant = {
        ...prev,
        [key]: value as any,
        isDirty: true,
      };

      if (key === 'path') {
        const resource = value;
        const defaultCrop =
          resource?.type === 'image' ? getDefaultCoverCrop(resource) : null;
        updated.creative_type = resource?.type ?? updated.creative_type;
        updated.crop = defaultCrop;
        updated.data_url = undefined;
        updated.config = undefined;

        if (resource?.type === 'video') {
          updated.logo = null;
          updated.svg = null;
        }

        if (resource?.type === 'image') {
          updated.svg = null;
        }

        if (resource === null) {
          updated.svg = null;
        }
      }

      if (key === 'svg') {
        // Sync config so the useEffect (which prefers config over svg)
        // never reloads from a stale snapshot after a canvas interaction.
        const json = canvas?.toJSON(
          Object.keys({ ...DEFAULT_FABRIC_PROPS, ...DEFAULT_BG_IMG_PROPS })
        );
        if (json) updated.config = JSON.stringify(json);
      }

      return updated;
    });

    // Generate data_url AFTER state update so the thumbnail re-renders with the correct crop/overlay.
    // Wrap in requestAnimationFrame so the pending requestRenderAll() from
    // applyCropToCanvas / setCanvasTo fires first, painting the canvas with the
    // latest objects *before* generateDataUrl captures the pixel buffer.
    if (key === 'svg') {
      requestAnimationFrame(() => generateDataUrl(currentUuid));
    }

    if (key === 'logo') {
      // Block the useEffect from reloading the canvas from stale config.
      // Without this, setCurrentVariant() above triggers useEffect([canvas,
      // currentVariant]) which calls canvas.loadFromJSON(oldConfig) and
      // overwrites the logo that changeLogo() is about to draw.
      skipCanvasReloadRef.current = currentUuid;
      changeLogo((value as any)?.path ?? null);
    }

    if (key === 'path' && value?.type === 'image') {
      // Rebuild overlay with the new image so canvas, SVG and thumbnail
      // stay in sync with the updated creative.
      // IMPORTANT: pass a variant snapshot that already includes the new path
      // so getOverlay uses the latest image, not the stale one from the
      // previous render.
      const resource = value;
      const defaultCrop = getDefaultCoverCrop(resource);
      const nextVariant: AdVariant = {
        ...(currentVariant as AdVariant),
        path: resource,
        creative_type: resource?.type ?? currentVariant?.creative_type ?? null,
        crop: defaultCrop,
        svg: null,
        config: undefined,
        data_url: undefined,
        ...(resource?.type === 'video' ? { logo: null, svg: null } : {}),
        ...(resource === null ? { svg: null } : {}),
      };

      /*
       * SYM-6020. This used to be `defaultAdOverlays[0]`, the first of the RAW list.
       *
       * For a switched-on customer that list holds the legacy rows and the v2 rows together, and
       * the legacy ones are years older -- so a brand new ad opened carrying an overlay that
       * customer can no longer choose, drawn for a square on a 4:5 canvas, with "Go to next step"
       * enabled over it.
       *
       * The choice moved into autoOverlayForNewAd() so it can be tested; this app has no component
       * tests, so a rule left inline here cannot be exercised at all.
       */
      // SYM-6776: an ad that already has a design it may use keeps it; only an ad without one
      // gets the automatic pick.
      const auto = overlayForNewPhoto(
        defaultAdOverlays,
        nextVariant,
        nextVariant.placement,
        nextVariant.creative_type,
        hasAdTemplatesV2
      );

      if (auto?.template_code) {
        /*
         * A v2 row is drawn on the SERVER. Compositing its svg would paint nothing -- the stored
         * one is a deliberate placeholder (seed_feed_ad_template_overlays migration) -- so the
         * template is SELECTED instead.
         *
         * ⚠️ SYM-6027. THIS DELIBERATELY DOES NOT CALL selectTemplate(), AND MUST NOT.
         *
         * selectTemplate() ends by asking the server to draw the template straight away. Run from
         * here that request goes out too early: requestTemplatePreview() reads the ad from
         * currentVariantRef (:1078), and that ref is only refreshed on render (:954) -- so it still
         * holds the ad WITHOUT the photo we are in the middle of setting. The request therefore
         * carries `path_id: null`, the server refuses every template that needs a picture, and the
         * customer gets one "Template ... requires a photo" toast per tile. Steven saw six of them
         * flash past on QA on 2026-08-19, and the template then only appeared once he opened the
         * Overlays panel, which asked again with the photo present.
         *
         * Setting the two fields and stopping is enough. The seeding effect keys on an identity
         * that includes the photo, so once this render commits it clears the stale previews and
         * asks again -- by which time the ad genuinely has its picture.
         */
        skipCanvasReloadRef.current = currentUuid;
        updateCurrentVariant('overlay_id', auto.id);
        updateCurrentVariant('template', auto.template_code);
      } else if (auto) {
        changeOverlay(auto.svg, nextVariant as any, defaultCrop);
      }
    }

    if (key === 'isDeleted' && value === true) {
      const nonDeteledVariant = variants.find(({ isDeleted }) => !isDeleted);
      if (nonDeteledVariant) setCurrentVariant(nonDeteledVariant);

      // Empty undo stack when switching between variants
      setSvgStack(Stack<string>());

      // Close side bar when switching variant
      setActiveObject(undefined);
    }

    return;
  };

  const deleteCurrentVariant = () => updateCurrentVariant('isDeleted', true);

  const addVariant = (variant: AdVariant) => {
    // Use functional (Immer draft) update so that consecutive calls in the
    // same event loop tick accumulate correctly instead of the last call
    // overwriting all previous ones (stale-closure bug).
    setVariants((draft) => {
      draft.push(variant);
    });
    setCurrentVariant(variant);
  };

  // TODO: based on old approach, can be cleaner
  const addVariantForPlacement = (placement: AdPlacement) => {
    const referenceVariant =
      variants.find(({ placement: p }) => p === placement) ?? variants[0];

    const bannerTitle = currentVacancy?.brand?.default_banner_title ?? 'WANTED';
    const city = currentVacancy?.targeting?.locations?.[0]?.city;

    addVariant({
      ...referenceVariant,
      id: undefined,
      /*
       * SYM-6020. + copies the neighbouring ad, so this says what a new one must NOT inherit --
       * including its TEMPLATE, which it used to keep while losing the picture that template needs.
       */
      ...NEW_AD_INHERITS_NOTHING,
      ...(placement === AdPlacement.REELS && {
        text: city
          ? `${bannerTitle}: ${currentVacancy?.title} in ${city}`
          : `${bannerTitle}: ${currentVacancy?.title}`,
      }),
      data_url: undefined,
      config: undefined,
      isDeleted: false,
      isDirty: true,
      placement,
      creative_type:
        placement === AdPlacement.REELS
          ? AdCreativeType.VIDEO
          : AdCreativeType.IMAGE,
      uuid: uniqueId(),
    });
  };

  /**
   * SYM-5797: pick a v2 template.
   *
   * Deliberately does NOT go near the canvas. The six v2 templates are drawn on the server so they
   * can measure text, resolve contrast and fit the layout -- things the canvas cannot do reliably --
   * which also means the canvas cannot draw them. So we record the choice on the variant and ask the
   * server for a picture of THIS variant, with its own photo and job title.
   *
   * The save sends overlay_id; the backend derives `template` from it, so the customer's pick and
   * the stored code can never drift apart. `template` is set locally too, purely so the tile can
   * show as selected without waiting for a round trip.
   */
  // Invalidate every preview when the thing being rendered changes: a different variant, or a new
  // photo on this one. Those are the only two events that make a render wrong -- picking a legacy
  // overlay is NOT one of them, and clearing there is what left the tiles stuck on "Rendering…".
  //
  // Keyed on `id`, NOT `uuid`, and this matters twice over. `uuid` is a client-side identity that
  // initVariants() regenerates on every refetch, so a SAVE handed every variant a new uuid while
  // its id stayed the same. This effect then cleared all six previews, while the panel only
  // re-requests when `id` changes -- so nothing refilled them and the tiles sat on "Rendering…"
  // forever. Two different identity keys for the same object is the bug; the panel keys on `id`,
  // so this must too. It also stops a save costing six pointless server renders.
  //
  // SYM-5908: the list moved into previewRenderIdentity() and gained the LOGO, which every
  // template draws and which was never here. Swapping the logo fired zero preview requests on QA
  // and left the customer looking at their old one. A dep array is the wrong home for a rule that
  // grows each time a template draws something new: adding to it is invisible and forgetting is
  // silent. The helper carries the reasoning and has tests; this line just consumes it.
  const renderIdentity = previewRenderIdentity(currentVariant);
  useEffect(() => {
    /*
     * SYM-6214. Seeded from the pictures the payload already carries, not emptied.
     *
     * SYM-5876 cleared this map on every ad change so the effects below would refill it by asking
     * the server. After this ticket the server has usually drawn them already -- at save time, on
     * the queue -- so the map starts full and nothing is asked for at all. A design the payload has
     * not prepared simply has no slot, which is the same state as before and reaches the same
     * request path.
     */
    /*
     * ⚠️ `currentVariant`, NOT currentVariantRef.current. The ref is assigned further down the
     * render body, so when this effect runs for a newly selected ad it still holds the PREVIOUS
     * one -- the map was then seeded empty, and the effect below asked the server for a design the
     * payload had already prepared. Caught by the "asks only for the designs the payload has not
     * prepared" test, which saw F1 requested for a fully prepared ad.
     */
    setTemplatePreviews(tileSlotsFromPayload(currentVariant));
    latestTileRequestRef.current = {};

    // SYM-6213: the customer has moved on, so tiles still waiting for a slot for the ad they left
    // are dropped rather than sent. A render already in flight finishes; its answer is ignored.
    previewQueueRef.current?.drop(
      (job) =>
        job.group.startsWith('tile:') && job.group !== `tile:${renderIdentity}`
    );
  }, [renderIdentity]);

  /**
   * Render the SELECTED template for this variant as soon as the editor has one, not when the
   * overlay panel happens to be opened.
   *
   * This lived in PlacementMenu, which the sidebar only mounts on the Overlays tab, so nothing was
   * requested until the customer clicked there. That made the main canvas promise "Rendering this
   * template on the server..." for a render nobody had asked for, and it sat like that forever.
   *
   * SYM-6213: ONLY the selected one. This effect used to ask for every design in the catalogue,
   * locked ones included, on every ad the customer clicked -- eight or nine renders per click before
   * the Templates panel had even been opened. The canvas needs exactly one of them. The others are
   * now asked for by the panel when it opens (PlacementMenu -> ensureTemplatePreviews), which is
   * the only moment anyone looks at them.
   *
   * Driven by whether the slot is MISSING, so it converges -- a request fills a slot immediately with
   * `loading`, so nothing is asked for twice, and anything that clears the slot is itself the
   * trigger to refill it.
   */
  useEffect(() => {
    /*
     * SYM-5919. `!currentVariant?.id` used to be part of this guard, and it is THE reason a newly
     * added or duplicated ad drew nothing: this effect is what fills all eight tiles, and it
     * declined to run at all until the ad had been saved.
     *
     * That gate was removed once already, in selectTemplate(), and removing it there changed
     * nothing measurable, because selectTemplate() calls ensureTemplatePreviews() which carried
     * the same gate, and because neither of them is what fills the panel on open. Measured on QA
     * after that first attempt shipped: still zero requests in 30 seconds, by both entry paths.
     * Three separate refusals, one removed, no observable difference.
     */
    if (!hasAdTemplatesV2 || !currentVariant) return;

    // SYM-5860: nothing to render for a variant the templates cannot apply to. Without this a
    // video variant fired six preview requests the server now refuses, and filled six tiles with
    // an error nobody should have been offered in the first place.
    if (
      !variantCanUseTemplates(
        currentVariant?.placement,
        currentVariant?.creative_type
      )
    ) {
      return;
    }

    /*
     * SYM-6035. An ad with no photo asks for nothing.
     *
     * Every template that needs a picture is refused with a 400 carrying "Template x requires a
     * photo.", and a 400 is not a quiet failure here: NotificationContext turns EVERY non-2xx into
     * a toast (:177-190). So adding an ad produced one red toast per template -- seven on feed,
     * eight on story -- before the customer had done anything wrong. Steven saw exactly that on QA
     * on 2026-08-24, on both placements.
     *
     * SYM-6035's earlier fix stopped the AUTO-APPLY path asking too early. This is the other
     * trigger, and it is independent: the seeding effect runs on any image variant in a supported
     * placement, whatever template it carries, so it fired regardless.
     *
     * ⚠️ A message is written rather than simply returning. Leaving the map empty parks every tile
     * on "Rendering…" forever, which is the state SYM-5919 records two people reading as a hang.
     *
     * ⚠️ B2 needs no photo and its render WOULD succeed, so this skips one tile that could have
     * drawn. Taken knowingly: the frontend has no way to ask which templates need a picture
     * (SYM-6024), and hard-coding the answer here would be a second copy of a fact the server owns
     * -- the mistake this epic has already made four times. When SYM-6024 lands, this becomes a
     * per-template check and B2 previews again.
     */
    // Keyed on the same photo previewRenderIdentity keys on, so when one lands the identity
    // changes, this effect re-runs, and the real requests go out.
    if (variantAwaitsPhotoForPreview(currentVariant)) {
      // The same map when every tile already says so: see withPhotoNeededNotices().
      setTemplatePreviews((prev) =>
        withPhotoNeededNotices(
          prev,
          defaultAdOverlays
            .filter(
              (o) =>
                o.template_code && o.placement === currentVariant?.placement
            )
            .map((o) => o.template_code as string)
        )
      );

      return;
    }

    const selected = currentVariant?.template;
    /*
     * SYM-6214. The payload is consulted directly, not through the mirror ref.
     *
     * templatePreviewsRef is refreshed during the NEXT render (see its assignment below), so on the
     * pass where an ad becomes current it still holds the previous ad's map. The seeding effect
     * above has already queued the payload's slots, but this effect cannot see them yet -- so it
     * asked the server for a design the server had already drawn. That is the single request this
     * ticket must not send, and the "fully prepared campaign asks for nothing" test is what caught
     * it.
     */
    const preparedSlots = tileSlotsFromPayload(currentVariant);

    /*
     * SYM-6214. "Has a slot" is no longer enough: a panel tile is drawn tile-sized, and shown on the
     * canvas it is blurry. So the design the ad carries is asked for again, full size, when the only
     * picture it has is a tile -- e.g. right after picking a design whose tile was already drawn.
     */
    if (
      selected &&
      !slotServesCanvas(templatePreviewsRef.current[selected]) &&
      !slotServesCanvas(preparedSlots[selected])
    ) {
      requestTemplatePreview(selected);
    }
  }, [
    hasAdTemplatesV2,
    // SYM-5919: `id` alone left this effect deaf to a switch between two UNSAVED ads, which both
    // have no id. renderIdentity falls back to uuid for exactly that case and is the same key the
    // effect above clears the map on, so the two now agree on when the ad underneath has changed.
    renderIdentity,
    currentVariant?.placement,
    currentVariant?.creative_type,
    currentVariant?.path?.id,
    currentVariant?.template,
    defaultAdOverlays,
    templatePreviews,
  ]);

  /**
   * Render each of $codes that this variant has no preview for yet.
   *
   * The picker calls this with all six when the panel opens, so the customer chooses between
   * pictures of their own ad rather than between codes like "S1" -- which mean nothing to anyone
   * who has not read the template registry. Each code is rendered once per variant and remembered,
   * so reopening the panel costs nothing.
   */
  const ensureTemplatePreviews = (codes: string[]) => {
    // SYM-5919: no id requirement. An ad that exists only in this tab can be drawn now, and this
    // gate is why removing the one in selectTemplate() on its own had no effect: that function
    // calls straight into here.
    if (!hasAdTemplatesV2 || !currentVariant) return;

    // SYM-6213: the panel is now the only thing that asks for the other designs, so it carries the
    // two refusals the seeding effect already makes. Without them a video, or an ad with no photo,
    // would send one doomed request per tile the moment the panel opened.
    if (
      !variantCanUseTemplates(
        currentVariant.placement,
        currentVariant.creative_type
      ) ||
      variantAwaitsPhotoForPreview(currentVariant)
    ) {
      return;
    }

    // SYM-6060: the locked designs are requested too. A faded tile is the real design at 40%
    // opacity, so it needs a render like any other. The selected one goes first.
    const selected = currentVariant.template;
    const ordered = selected
      ? [
          ...codes.filter((c) => c === selected),
          ...codes.filter((c) => c !== selected),
        ]
      : codes;

    /*
     * SYM-6214. The pictures the server drew on save, read straight from the ad.
     *
     * Same trap as the selected-design effect above: templatePreviewsRef is refreshed on the NEXT
     * render, so when the panel mounts in the same pass as the ad becoming current, the seeded
     * slots are not visible through it yet -- and every prepared design would be asked for again.
     * That is the wait this ticket removes, so it is read from the payload directly.
     */
    const preparedSlots = tileSlotsFromPayload(currentVariant);

    ordered.forEach((code) => {
      // A slot exists once a render is in flight, has landed, or has failed with a reason. Only a
      // code with no slot at all needs asking for.
      if (templatePreviewsRef.current[code] || preparedSlots[code]) return;
      requestTemplatePreview(code);
    });
  };

  // Kept in sync every render so requestTemplatePreview() never reads a stale variant.
  currentVariantRef.current = currentVariant;
  templatePreviewsRef.current = templatePreviews;

  /**
   * Re-render the previews after a value changed. Debounced, because every keystroke in the value
   * panel would otherwise cost a server render.
   *
   * SYM-5876: ALL SIX are rendered with the current values, so a change invalidates all six -- not
   * just the selected one. Re-rendering only the selection left the other five showing the ad as it
   * was when the panel opened, which is how a customer could type a hook line, watch the template
   * that renders it not change, and conclude the edit had not saved.
   *
   * The others are dropped rather than re-requested here: the seeding effect above is driven by
   * which slots are MISSING, so removing them IS the request, and it refills them selected-first.
   * The selected slot is kept and re-requested directly, because it also drives the main canvas and
   * blanking it on every keystroke would flicker exactly where the customer is looking.
   */
  const refreshTemplatePreview = useDebouncedCallback(() => {
    const variant = currentVariantRef.current;
    const code = variant?.template;
    // SYM-5919: the third gate. Without removing it, an unsaved ad would draw its tiles once and
    // then freeze -- every later edit to a value would leave the picture behind, which is the
    // SYM-5961 complaint reappearing on new ads only.
    if (!code || !variant) return;

    setTemplatePreviews((prev) => previewsAfterValueChange(prev, code));
    requestTemplatePreview(code);
  }, 600);

  const selectTemplate = (overlay: AdOverlay) => {
    const code = overlay.template_code;
    if (!code || !currentVariant) return;

    // Both writes land on the variant, which re-renders the provider and fires the canvas-reload
    // effect. That effect would call loadFromJSON(config) and repaint the legacy overlay
    // underneath. It does not matter visually while a template covers the canvas, but it would
    // leave the canvas holding a different overlay than the one the customer last saw, so tell it
    // to stand down -- same reason the logo path does.
    skipCanvasReloadRef.current = currentVariant.uuid;
    updateCurrentVariant('overlay_id', overlay.id);
    updateCurrentVariant('template', code);

    // SYM-5919: no longer gated on the variant being saved. The vacancy-scoped endpoint renders an
    // ad that exists only in this tab, so the tiles fill in for a brand new variant too.
    ensureTemplatePreviews([code]);
  };

  /**
   * SYM-5990. Record the colours a finished render says it drew with.
   *
   * ⚠️ DELIBERATELY NOT updateCurrentVariant(). That marks the ad dirty, and this write is the
   * SERVER answering, not the customer editing: routed through it, simply opening an ad would
   * raise "Unsaved changes" on every one of the eight previews the panel fires on open, and Save
   * would offer to persist a change nobody made.
   *
   * Written by uuid rather than onto currentVariant, because a render takes a few hundred
   * milliseconds and the customer may have clicked another ad in the rail by the time it lands.
   * Whether the answer is still current is decided by resolvedColoursFromPreview.
   */
  const applyResolvedColours = (
    uuid: string | undefined,
    sent: { primary?: string | null; accent?: string | null },
    response:
      | {
          resolved_primary_color?: string | null;
          resolved_accent_color?: string | null;
        }
      | null
      | undefined
  ) => {
    if (!uuid) return;

    setVariants((draft) => {
      const variant = draft.find((v) => v.uuid === uuid);
      if (!variant) return;

      const colours = resolvedColoursFromPreview(sent, variant, response);
      if (!colours) return;

      if (colours.primary) variant.resolved_primary_color = colours.primary;
      if (colours.accent) variant.resolved_accent_color = colours.accent;
    });

    /*
     * AND the displayed copy, which is where the first attempt at this fix went wrong.
     *
     * `currentVariant` is not a view onto `variants`. It is separate state holding a
     * structuredClone, so that frozen Immer proxies never leak downstream, and the two pickers read
     * THAT. Writing only to the array above changed nothing on screen: the fix shipped, the
     * qa-triage spec stayed red, and the field went on showing the loaded colour. Every other
     * writer in this file updates both, updateCurrentVariant included.
     */
    setCurrentVariant((prev) => {
      if (!prev || prev.uuid !== uuid) return prev;

      const colours = resolvedColoursFromPreview(sent, prev, response);
      if (!colours) return prev;

      const primary = colours.primary ?? prev.resolved_primary_color;
      const accent = colours.accent ?? prev.resolved_accent_color;

      /*
       * Same object when nothing moved, and this is not a micro-optimisation.
       * setCurrentVariant feeds useEffect([canvas, currentVariant]), which reloads the canvas and
       * recaptures a thumbnail. All eight tiles answer for one colour change, so a fresh object per
       * response would do that eight times for a single click.
       */
      if (
        primary === prev.resolved_primary_color &&
        accent === prev.resolved_accent_color
      ) {
        return prev;
      }

      return {
        ...prev,
        resolved_primary_color: primary,
        resolved_accent_color: accent,
      };
    });
  };

  /** Ask the server for a picture of this variant rendered with $code. */
  const requestTemplatePreview = (code: string) => {
    const variant = currentVariantRef.current ?? currentVariant;
    if (!variant) return;

    /*
     * SYM-5919. An ad the customer has just added or duplicated has no id, and this used to give
     * up here. The editor was right that the server could not look it up, but the panel had no way
     * to say so, so eight tiles sat on "Rendering..." forever and two people read that as a hang.
     *
     * There are now two endpoints. The variant-scoped one previews a STORED ad by id; the
     * vacancy-scoped one takes the whole ad in the body, which is the only way to draw something
     * that exists nowhere but this browser tab. Neither persists anything.
     */
    const isUnsaved = !variant.id;

    // Nothing to hang ownership off, and no vacancy to render against.
    if (isUnsaved && !activeVacancy) return;

    /*
     * SYM-6214. Full size only for the design the ad carries, because only that one is shown large
     * (the canvas). Every other design is a panel tile at 128px, drawn 256px tall: ~0.25s instead of
     * ~0.7s on QA, 16 Sep 2026, which is what frees the two server slots the panel queues on. The
     * server still draws a design the plan does not include tile-sized, whatever is asked.
     */
    const size = code === variant.template ? 'full' : 'tile';

    // An upgrade from a tile to the full-size picture keeps showing the tile meanwhile, rather than
    // blanking the canvas for the length of a render. The slot records what is on its way, so the
    // canvas effect can tell a tile in flight from the picture it actually needs.
    setTemplatePreviews((prev) => ({
      ...prev,
      [code]:
        size === 'full' && prev[code]?.url && !prev[code]?.full
          ? { loading: true, url: prev[code]?.url, full: true }
          : { loading: true, full: size === 'full' },
    }));

    const run = isUnsaved ? previewUnsavedAdTemplate : previewAdTemplate;
    const input = { ...templatePreviewInput(variant, code, isUnsaved), size };

    /*
     * SYM-6213. Through the shared queue, never straight out.
     *
     * The key carries the ad and the values being drawn, so the same picture is never asked for
     * twice at once, while a genuinely new edit still goes out. The ad's identity is captured NOW:
     * if the customer has moved to another ad by the time a slot frees up or an answer lands, the
     * request is dropped or its answer ignored, rather than painting ad 3's picture into ad 1's
     * tiles.
     */
    const identity = previewRenderIdentity(variant);
    const requestKey = `tile:${identity}:${code}:${JSON.stringify(input)}`;
    /*
     * SYM-6214 (Astra's third review, point 2.4). "Latest wins" per design AND size. Keyed on the
     * design alone, a tile request sent after the full-size one silenced the full-size answer --
     * the one the canvas was waiting for -- simply because it was sent later. The two sizes are
     * separate conversations; which picture the slot ends up with is decided where they land.
     */
    const latestKey = `${code}:${size}`;
    latestTileRequestRef.current[latestKey] = requestKey;
    const stillWanted = () =>
      previewRenderIdentity(currentVariantRef.current) === identity &&
      latestTileRequestRef.current[latestKey] === requestKey;

    schedulePreview(previewQueueRef.current!, {
      key: requestKey,
      group: `tile:${identity}`,
      // The picture on the canvas first, then the rest of the panel. The rail waits behind both.
      priority: code === variant.template ? 0 : 1,
      stillWanted,
      call: () =>
        run({
          variables: {
            ...(isUnsaved
              ? { vacancyId: activeVacancy }
              : { variantId: variant.id }),
            input,
          },
        }),
      onSuccess: (result) => {
        if (!stillWanted()) return;

        const preview = (result?.data as any)?.templatePreview;
        setTemplatePreviews((prev) =>
          // A tile never replaces the full-size picture, nor one on its way: whichever order the two
          // answers land in, the canvas ends up with the one it can show sharp.
          size === 'tile' && prev[code]?.full
            ? prev
            : {
                ...prev,
                [code]: { url: preview?.url, full: size === 'full' },
              }
        );

        /*
         * SYM-5990. Take the colours off the render that just came back.
         *
         * The two pickers display the RESOLVED colour, because an ad with none of its own has to
         * show its brand's rather than a black it never chose (SYM-5929). That value arrives with
         * the variant and is refreshed only by a save, so clicking black turned the ad black and
         * left the field reading the previous colour until the customer saved -- which reads as
         * "my change did not take", the same conclusion SYM-5961 produces from the halves swapped.
         *
         * The server resolves this to draw the picture, so it is asked rather than re-derived
         * here. Restating the chain in the frontend is how SYM-5875's field list, SYM-5908's dep
         * array and SYM-5912's band heights each drifted.
         */
        applyResolvedColours(
          variant.uuid,
          { primary: variant.primary_color, accent: variant.accent_color },
          preview
        );
      },
      /*
       * SYM-6247. The server asked for a wait too long to spin through -- the per-user limit is per
       * minute -- and the queue has arranged the retry for when it is over. The tile says it is
       * waiting rather than spinning (SYM-5919: a long spinner reads as a hang) or stopping on a
       * message nobody can act on (production, 17 Sep 2026). A picture already on the slot stays.
       */
      onWaiting: () => {
        if (!stillWanted()) return;

        setTemplatePreviews((prev) => ({
          ...prev,
          [code]: {
            ...prev[code],
            loading: true,
            waiting: true,
            full: size === 'full' || !!prev[code]?.full,
          },
        }));
      },
      onFailure: (error: any) => {
        if (!stillWanted()) return;

        // The endpoint answers 400 with the reason -- capability off, a placement this template
        // cannot render, a photo-requiring template with no photo. Surface it rather than leaving a
        // spinner running. SYM-6213: a "busy" answer only lands here once its retries are used up.
        /*
         * ⚠️ A busy answer never borrows the network layer's wording.
         *
         * Measured on QA, 16 Sep 2026: with the render slots saturated, an exhausted retry put
         * Apollo's own "Response not successful: Received status code 429" on a tile, in front of
         * the customer. The guard messages from the endpoint are ours and explain themselves; a
         * 503 or 429 that ran out of retries is a "try again", not a refusal with a reason.
         */
        const busy = isPreviewBusy(error);
        const healKey = `${identity}:${code}`;
        const willHeal =
          busy && (busyHealsRef.current[healKey] ?? 0) < BUSY_HEALS;
        setTemplatePreviews((prev) => ({
          ...prev,
          [code]: {
            error: busy
              ? willHeal
                ? BUSY_PREVIEW_MESSAGE
                : BUSY_PREVIEW_FINAL_MESSAGE
              : previewFailureMessage(error),
          },
        }));

        /*
         * SYM-6247. A tile that ended busy heals itself. The old wording asked the customer to
         * "open the panel again", which asks for nothing: a slot holding a message counts as
         * answered, so neither the panel nor the canvas ever asked again. Clearing the slot IS the
         * request -- both effects that fill slots are driven by which ones are missing. At most
         * BUSY_HEALS times per ad and design, half a minute apart, and only while this is still the
         * ad and the request the customer is looking at.
         */
        if (willHeal) {
          busyHealsRef.current[healKey] =
            (busyHealsRef.current[healKey] ?? 0) + 1;
          setTimeout(() => {
            if (!stillWanted()) return;
            setTemplatePreviews((prev) => {
              if (prev[code]?.error !== BUSY_PREVIEW_MESSAGE) return prev;
              const rest = { ...prev };
              delete rest[code];
              return rest;
            });
          }, BUSY_HEAL_AFTER_MS);
        }
      },
    });
  };

  /**
   * SYM-5975. Fill the RAIL: one render per saved ad that carries a template.
   *
   * Steven reloaded a campaign and none of the thumbnails showed their template. Only the ad being
   * edited could be answered, so every other one fell back to a canvas capture taken before the
   * server ever drew the template over it. Clicking a thumbnail made it correct itself, which is
   * the worst shape available: right under the cursor, wrong at a glance.
   *
   * ⚠️ DELIBERATELY LAST, AFTER the seeding effect above, and the ordering is the cost control.
   *
   * This costs one request per templated ad. Measured on QA on 2026-08-12: a render answers in
   * ~440ms cold and ~320ms warm, the difference being the SYM-5853 cache, which skips the
   * rasterise, the upload and the presign but not the SVG build. Campaign 6786 was carrying 11
   * story ads, so this is a real addition on top of the 8 the open already fires.
   *
   * React runs effects in declaration order, so the tiles for the ad the customer is LOOKING AT are
   * requested first and the rail fills in behind them. That does not reduce the server's work; it
   * decides who waits, which is the part the customer notices.
   *
   * SYM-6213: requested ONCE per ad and content, and remembered from the moment it is asked for --
   * see railRequestedRef for why the old "is there a url yet" guard multiplied requests. A failure
   * is not retried on the next change to the list; a "busy" answer is retried a few times by the
   * queue, and after that the thumbnail keeps its stored artefact.
   *
   * The ad being EDITED is skipped: its selected template is already rendered for the canvas, and
   * railPreviewUrl() answers its thumbnail from that. Asking the rail as well drew it twice.
   */
  useEffect(() => {
    if (!hasAdTemplatesV2) return;

    variants.forEach((variant) => {
      if (!variant.id || !variant.template || variant.isDeleted) return;
      if (!variantCanUseTemplates(variant.placement, variant.creative_type)) {
        return;
      }
      if (variant.uuid === currentVariant?.uuid) return;

      /*
       * SYM-6214. An ad the server has already drawn is never asked about.
       *
       * railPreviewUrl() shows that stored picture, so a request here would fetch a second copy of
       * something already on screen -- which is precisely the volume SYM-6213 spent an outage
       * learning to avoid. The rail keeps asking only for ads saved before this shipped, or ones
       * whose render is still on the queue.
       */
      if (!railNeedsRequest(variant)) return;

      const key = railPreviewKey(variant);
      if (!key || railRequestedRef.current[key]) return;

      railRequestedRef.current[key] = 'requested';
      const { id, template } = variant;

      schedulePreview(previewQueueRef.current!, {
        key: `rail:${key}`,
        group: 'rail',
        priority: 2,
        stillWanted: () => true,
        call: () =>
          previewAdTemplate({
            variables: {
              variantId: id,
              // Only the template. Everything else the renderer needs is on the row, because this
              // ad is SAVED -- that is the whole difference from the unsaved path, which has to
              // carry the entire ad in the body.
              // SYM-6214: tile-sized. A rail thumbnail is small, and a full-size draw holds one of
              // the server's two render slots about three times as long.
              input: { template, size: 'tile' },
            },
          }),
        onSuccess: (result) => {
          const url = (result?.data as any)?.templatePreview?.url;
          if (!url) {
            railRequestedRef.current[key] = 'failed';
            return;
          }
          railRequestedRef.current[key] = 'done';
          setRailPreviews((prev) => ({ ...prev, [key]: url }));
        },
        onFailure: () => {
          // Swallowed on purpose. A thumbnail that cannot be rendered falls back to the stored
          // artefact, which is what the rail showed before this existed. Surfacing an error per
          // ad would put failures on ads the customer is not even looking at.
          railRequestedRef.current[key] = 'failed';
        },
      });
    });
  }, [hasAdTemplatesV2, variants, currentVariant?.uuid, previewAdTemplate]);

  /**
   * The customer explicitly picks a LEGACY overlay.
   *
   * Deliberately separate from changeOverlay(): that is also called internally when the image
   * changes, and clearing the template there would silently drop a customer's chosen template
   * behind their back. Only an explicit pick should undo an explicit pick.
   *
   * Clearing matters because the backend derives `template` from overlay_id -- but only when
   * overlay_id is sent. Without this, switching from a v2 template back to a legacy overlay left
   * the stale template on the variant and the server kept rendering v2, ignoring the new choice.
   */
  const pickLegacyOverlay = (overlay: AdOverlay) => {
    if (currentVariant?.template || currentVariant?.overlay_id) {
      // MUST come before the writes. updateCurrentVariant() re-renders the provider, which fires
      // useEffect([canvas, currentVariant]) -> canvas.loadFromJSON(config), and that repaints
      // whatever overlay `config` was last captured with -- undoing the changeOverlay() below.
      // Symptom: the first legacy overlay you click sticks, and every later click snaps back to it,
      // because `config` was baked with the first one. The logo path documents the same trap.
      // The old code reached changeOverlay() directly and never wrote to the variant, so this only
      // became reachable once picking a legacy overlay had to clear the v2 template.
      skipCanvasReloadRef.current = currentVariant.uuid;
      updateCurrentVariant('overlay_id', overlay.id);
      updateCurrentVariant('template', null);
    }
    // Deliberately does NOT clear templatePreviews. They are renders of THIS variant under each
    // template, and picking a legacy overlay changes neither the photo nor the job title, so they
    // stay valid -- while clearing them stranded all six tiles on "Rendering…" forever, because
    // the panel only requests previews when the variant or the code list changes and neither had.
    // The big canvas layer hides on its own: it keys off currentVariant.template, now null.
    changeOverlay(overlay.svg);
  };

  const changeOverlay = (
    ov: string,
    variantOverride?: AdVariant,
    cropOverride?: Crop | null
  ) => {
    if (!canvas) return;
    const location = currentVacancy?.targeting?.locations?.[0]?.city;
    const variantForOverlay = variantOverride ?? currentVariant;
    const overlay = getOverlay(
      ov,
      variantForOverlay,
      currentVacancy?.brand,
      location
    );

    (async () => {
      await setCanvasTo(overlay);
      if (cropOverride) {
        applyCropToCanvas(cropOverride, true);
      }
    })();
  };

  /**
   * Read an SVG Image
   * @param svg => the image in svg format
   *
   */

  const setCanvasTo = async (svg: string) => {
    if (!canvas || !currentVariant) return;

    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svg, 'image/svg+xml');

    //Remove the rectangle (the rectangle is normally the background color of the text)
    //Set the background color of the object fabric.Text
    //&& get preview image (Placement menu)
    svgDoc.querySelectorAll('g').forEach((group) => {
      const rect = group.querySelectorAll('rect');
      const text = group.querySelector('text');

      if (rect[0] && text) {
        const bgColor = rect[rect.length - 1].getAttribute('fill');
        if (!bgColor) return;
        text.setAttribute('style', `data-bg-color: ${bgColor}`);
        rect.forEach((v) => group.removeChild(v));
      }
    });

    // Set crossOrigin on SVG <image> elements so fabric.js loads them with
    // CORS from the start — prevents a tainted canvas.
    svgDoc.querySelectorAll('image').forEach((imgEl) => {
      imgEl.setAttribute('crossorigin', 'anonymous');
    });

    //Old variants do still have tspan in their SVG -> remove them!
    const svgContent = serializer.serializeToString(svgDoc.documentElement);
    if (svgContent.includes('tspan'))
      svgDoc.querySelectorAll('g > text').forEach((txt) => {
        const originalSvg = serializer.serializeToString(txt.parentNode!);
        const transformedSvg = deleteTspan(originalSvg);

        const parent = txt.parentNode;
        if (parent && parent.parentNode) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(transformedSvg, 'image/svg+xml');
          const newG = doc.documentElement;
          parent.parentNode.replaceChild(newG, parent);
        }
      });

    /**
     * SYM-6010. Resize artwork that is the right shape at the wrong size, before fabric reads it.
     *
     * Done here, on the markup, rather than object-by-object afterwards on purpose. Fabric treats
     * text differently from shapes -- the loop below deliberately resets a text object's scale to 1
     * and recomputes its width -- so scaling each object would need a special case per type and
     * would fight that loop. Wrapping the whole drawing in one transform is the file's own way of
     * saying the same thing, and it reaches text, shapes and embedded images alike.
     *
     * Only ever applied to artwork that ALREADY has the frame's proportions, so nothing distorts:
     * getOverlayFit() returns 'mismatch' for anything else and the panel does not offer it.
     *
     * ⚠️ UNVERIFIED IN A BROWSER. Measured on production 2026-08-24, every piece of custom artwork
     * is exactly its frame's size, so this branch does not fire for any real overlay today and
     * could not be exercised end to end. The decision that leads here is unit-tested; the render is
     * not, because fabric needs a canvas binding the test environment does not build. Check it
     * against a real, deliberately mis-sized file in QA before relying on it.
     */
    const fit = getOverlayFit(svg, canvasWidth, canvasHeight);
    if (fit.kind === 'scale') {
      const root = svgDoc.documentElement;
      const group = svgDoc.createElementNS('http://www.w3.org/2000/svg', 'g');
      group.setAttribute('transform', `scale(${fit.factor})`);
      while (root.firstChild) group.appendChild(root.firstChild);
      root.appendChild(group);
    }

    const ctx = canvas.getContext();
    if (ctx) {
      canvas.clearContext(ctx);
      canvas.clear();
    }

    await (async () => {
      try {
        const objects = await new Promise<fabric.Object[]>(
          (resolve, reject) => {
            // crossOrigin: 'anonymous' is critical — without it Fabric.js
            // loads images without CORS and the canvas becomes tainted. SYM-5273.
            (fabric.loadSVGFromString as any)(
              serializer.serializeToString(svgDoc.documentElement),
              (objects: fabric.Object[]) => {
                if (objects) resolve(objects);
                else reject(new Error('Reading SVG failed'));
              },
              null, // reviver
              { crossOrigin: 'anonymous' }
            );
          }
        );

        objects.forEach((v, i) => {
          v.set({ ...DEFAULT_FABRIC_PROPS });

          // Make sure the background image is not moveable
          if (i === 0 && v.type === 'image')
            (v as fabric.Image).set(DEFAULT_BG_IMG_PROPS);

          if (v.type === 'text') {
            const textB = v as fabric.Text;
            textB.set({
              text: textB.text!.replaceAll('{NEW_LINE_BREAK}', '\n'),
              textLines: textB.text!.includes('{NEW_LINE_BREAK}')
                ? textB.textLines
                : [textB.text!],
            });

            const width1 = textB.getBoundingRect().width + 1;
            const width2 = textB.textLines.reduce(
              (max, _, i) => Math.max(max, textB.getLineWidth(i)),
              0
            );
            const finalWidth =
              textB.textLines.length > 1 ? width1 : Math.max(width1, width2);

            textB.set({
              scaleX: 1,
              scaleY: 1,
              width: finalWidth,
            });

            if ((textB as any).customAlign) {
              textB.set('textAlign', (textB as any).customAlign);
            }

            if ((textB as any).positionOnCanvas) {
              const canvasPos = (textB as any).positionOnCanvas;
              const canvasWidth = canvas.getWidth();
              const getLeftPosition = () => {
                // Use different anchor points per alignment (matches overlayHelpers.ts when fabric=false):
                // - left: keep SVG position (left edge at margin)
                // - middle: center at 50% of canvas
                // - right: right edge at 97.5% (2.5% margin)
                const currentLeft = textB.left ?? 0;
                const rightEdgeAnchor = canvasWidth - canvasWidth * 0.025;
                const centerAnchor = canvasWidth / 2;

                if (canvasPos === 'middle') {
                  return centerAnchor - finalWidth / 2;
                }
                if (canvasPos === 'right') {
                  return rightEdgeAnchor - finalWidth;
                }
                return currentLeft;
              };
              textB.set('left', getLeftPosition());
            }
          }

          canvas.add(v);
        });

        canvas.requestRenderAll();
        await new Promise((r) => requestAnimationFrame(r));
      } catch (error) {
        console.error('Error:', error);
      }
    })();

    if (svg !== currentVariant.svg) canvas.fire('object:modified');
  };

  const undoChange = () => {
    const topItem = svgStack.first();
    if (topItem) setCanvasTo(topItem).then(() => setSvgStack(svgStack.pop()));
  };

  // Keep fabric.Canvas internal dimensions in sync with the React-computed
  // canvasWidth / canvasHeight.  On the very first render currentVariant is
  // undefined, so the helpers return 1×1.  Once variants load the dimensions
  // jump to the real values (e.g. 1000×1000 for feed) and the canvas MUST be
  // resized before any content is loaded onto it, otherwise toDataURL()
  // captures a 1-pixel solid-colour image.
  useEffect(() => {
    if (!canvas || canvasWidth <= 1 || canvasHeight <= 1) return;
    canvas.setDimensions({ width: canvasWidth, height: canvasHeight });
    canvas.requestRenderAll();
  }, [canvas, canvasWidth, canvasHeight]);

  // Set canvas when currentVariant changes
  useEffect(() => {
    if (!canvas || !currentVariant) return;

    // Skip reload when the change came from a canvas interaction (drag/resize)
    // on the SAME variant. The canvas already has the correct state.
    // If the UUID doesn't match (variant switch), reload normally.
    if (skipCanvasReloadRef.current === currentVariant.uuid) {
      skipCanvasReloadRef.current = null;
      // Still generate the data_url thumbnail so the preview updates.
      requestAnimationFrame(() => generateDataUrl(currentVariant.uuid));
      return;
    }
    skipCanvasReloadRef.current = null;

    if (currentVariant.config) {
      // Inject crossOrigin into every image object so fabric.js loads them
      // with CORS from the start — prevents a tainted canvas that would make
      // toDataURL() throw or return a blank frame.
      let configObj: any;
      try {
        configObj =
          typeof currentVariant.config === 'string'
            ? JSON.parse(currentVariant.config)
            : currentVariant.config;
        if (configObj?.objects) {
          configObj.objects = configObj.objects.map((obj: any) =>
            obj.type === 'image' ? { ...obj, crossOrigin: 'anonymous' } : obj
          );
        }
      } catch {
        configObj = currentVariant.config;
      }

      canvas.loadFromJSON(configObj, () => {
        canvas.requestRenderAll();
        // Wait one animation frame so the browser paints the freshly-rendered
        // canvas before we capture it as a data-URL thumbnail.
        requestAnimationFrame(() => generateDataUrl(currentVariant.uuid));
      });
    } else if (currentVariant.svg) {
      setCanvasTo(currentVariant.svg).then(() => {
        requestAnimationFrame(() => generateDataUrl(currentVariant.uuid));
      });
    }
  }, [canvas, currentVariant]);

  // When the sidebar closes (activeTab becomes undefined) the EditorWrapper
  // transitions back to visible.  Schedule a render to make sure the canvas
  // pixel buffer reflects the latest state.
  useEffect(() => {
    if (!activeTab && canvas) {
      canvas.requestRenderAll();
    }
  }, [activeTab, canvas]);

  const addToCanvas = (element: fabric.Object) => {
    canvas?.add(element);
    canvas?.setActiveObject(element);
    triggerCanvasEvent();
  };

  const MAX_LOGO_HEIGHT = 120;
  const MAX_LOGO_WIDTH = 150;

  const changeLogo = (logoPath?: string | null) => {
    if (!canvas) return;

    // Capture UUID now — by the time fromURL's async callback fires, the
    // component may have re-rendered and currentVariant could point elsewhere.
    const capturedUuid = currentVariant?.uuid;

    // Remove old logo
    const object = getCanvasObject('logo');
    if (object) {
      canvas?.remove(object);
      triggerCanvasEvent();
    }

    // Persist the updated canvas state (without old logo, or with new logo
    // once fromURL completes) so that future variant switches reload correctly.
    // We call setVariants directly rather than updateCurrentVariant to avoid
    // triggering the useEffect([canvas, currentVariant]) again.
    const persistConfig = () => {
      if (!capturedUuid) return;
      const json = canvas.toJSON(
        Object.keys({ ...DEFAULT_FABRIC_PROPS, ...DEFAULT_BG_IMG_PROPS })
      );
      setVariants((draft) => {
        const variant = draft.find((v) => v.uuid === capturedUuid);
        if (variant) variant.config = JSON.stringify(json);
      });
    };

    // Add new logo (if any present).
    // logoPath is passed directly from updateCurrentVariant to avoid reading
    // stale state — setCurrentVariant is async so currentVariant?.logo?.path
    // would still hold the OLD value at this point in the call stack.
    const logo = logoPath !== undefined ? logoPath : currentVariant?.logo?.path;
    if (logo) {
      fabric.Image.fromURL(
        logo,
        (img) => {
          const { width, height } = img;

          // First, scale to fit within max dimensions
          const scaleX = MAX_LOGO_WIDTH / width!;
          const scaleY = MAX_LOGO_HEIGHT / height!;
          let scale = Math.max(scaleX, scaleY);

          // check scaled width against canvas constraints and handle overflow
          const scaledWidth = width! * scale;
          if (scaledWidth > canvasWidth) scale = (canvasWidth * 0.75) / width!;

          img.set({
            ...DEFAULT_FABRIC_PROPS,
            top: 25,
            left: canvasWidth - 25 - width! * scale,
          });

          img.scale(scale);

          addToCanvas(img);
          persistConfig();
        },
        { crossOrigin: 'anonymous' }
      );
    } else {
      // Logo removed — persist canvas state without it.
      persistConfig();
    }
  };

  const addCanvasElement = (type: string) => {
    switch (type) {
      case 'text':
        addToCanvas(
          new fabric.Text('New Text', {
            ...DEFAULT_FABRIC_PROPS,
            fontFamily: 'Arial',
            fontSize: 56,
            fontWeight: 700,
            textAlign: 'left',
            fill: '#000000',
            backgroundColor: 'transparent',
            type: 'text',
            centeredScaling: false,
          })
        );
        break;
      case 'rectangle':
        addToCanvas(
          new fabric.Rect({
            ...DEFAULT_FABRIC_PROPS,
            width: 400,
            height: 150,
            fill: '#ffffff',
            type: 'rect',
          })
        );
        break;
      case 'logo':
        changeLogo();
        break;
      default:
        break;
    }
  };

  const triggerCanvasEvent = useDebouncedCallback(() => {
    canvas?.fire('object:modified');
  }, 500);

  const updateActiveObject = (key: keyof fabric.Object, value: any) => {
    const active = canvas?.getActiveObject();
    if (!active) return;

    active.set({ [key]: value });

    // Clone the object to ensure React detects a state change
    const updatedActiveObject = Object.assign(
      Object.create(Object.getPrototypeOf(active)),
      active
    );

    triggerCanvasEvent();
    setActiveObject(updatedActiveObject ?? undefined);
  };

  const removeActiveObject = () => {
    if (!activeObject) return;

    canvas?.remove(activeObject);
    triggerCanvasEvent();

    setActiveObject(undefined);
  };

  const duplicateActiveObject = () => {
    if (!activeObject) return;

    activeObject.clone((clonedObject: fabric.Object) => {
      // Offset the new object slightly to distinguish it visually
      clonedObject.set({
        left: activeObject.left! + 40,
        top: activeObject.top! + 40,
      });

      addToCanvas(clonedObject);
    });
  };

  const setActiveObject = (object?: fabric.Object) => {
    _setActiveObject(object);

    if (object) {
      tabOpener.open(
        () => canvas?.getActiveObject() === object,
        () => setActiveTab(tabForObject(object.type))
      );
    } else {
      tabOpener.cancel();
      canvas?.discardActiveObject();
      setActiveTab(undefined);
    }

    canvas?.requestRenderAll(); // Needed for resetting "active" border
  };

  useEffect(() => {
    canvas?.on('object:modified', () => {
      // Guard: never persist a degenerate 1×1 SVG produced when the canvas
      // hasn't been sized yet (placement still undefined).
      if (canvas.getWidth() <= 1 || canvas.getHeight() <= 1) return;
      updateCurrentVariant('svg', canvas?.toSVG());
    });

    const handleSelectionChange = () => {
      // Multi-select measure
      if (canvas?.getActiveObjects()?.length! > 1) {
        tabOpener.cancel();
        setActiveTab(AdEditorSideBarTabs.VISUAL_OPTIONS);
        return;
      } else {
        setActiveObject(canvas?._activeObject ?? undefined);
      }
    };

    canvas?.on('selection:created', handleSelectionChange);
    canvas?.on('selection:updated', handleSelectionChange);
    canvas?.on('selection:cleared', handleSelectionChange);

    const handleClickOutside = (event: MouseEvent) => {
      if (event.target === document.getElementById('container'))
        setActiveObject(undefined);
    };
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      canvas?.off('object:modified');
      canvas?.off('selection:created');
      canvas?.off('selection:updated');
      canvas?.off('selection:cleared');

      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [canvas, currentVariant]);

  const sendBackwards = () => {
    activeObject?.sendBackwards();
    triggerCanvasEvent();
  };

  const bringForward = () => {
    activeObject?.bringForward();
    triggerCanvasEvent();
  };

  return (
    <AdEditorContext.Provider
      value={{
        setCanvas,
        activeTab,
        currentVariant,
        defaultAdOverlays,
        customAdOverlays,
        payload,
        previewChannel,
        setActiveTab,
        setPayload,
        setPreviewChannel,
        setUploadMode,
        btlCropMinimum,
        setBtlCropMinimum,
        initVariants,
        applyStoredPictures,
        applyToVariants,
        markClipSeen,
        deleteCurrentVariant,
        addVariant,
        addVariantForPlacement,
        duplicateCurrentVariant,
        updateCurrentVariant,
        setCurrentVariant,
        uploadMode,
        variants,
        videoRef,
        svgStack,
        addCanvasElement,
        undoChange,
        changeOverlay,
        selectTemplate,
        pickLegacyOverlay,
        templatePreviews,
        railPreviews,
        ensureTemplatePreviews,
        refreshTemplatePreview,
        canvasWidth,
        canvasHeight,
        hasAdTemplatesV2: portraitFeed,
        getCanvasObject,
        activeObject,
        updateActiveObject,
        removeActiveObject,
        duplicateActiveObject,
        cropBackground,
        cropBackgroundComplete,
        getBackgroundCrop,
        sendBackwards,
        bringForward,
      }}
    >
      {children}
    </AdEditorContext.Provider>
  );
};
